import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  catalogResponseSchema,
  inventoryStatusSchema,
  inventoryUnitsResponseSchema,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { itemName } from './itemName.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';
import {
  availableUnitActions,
  HeldUnitActionsArea,
  UnitActionPanel,
  type UnitAction,
} from './UnitActions.js';

/**
 * The serialized registry (REQUIREMENTS §2.6): every physical unit, searchable
 * by serial. The search box is scan-friendly (§3.9 answered "later"): a
 * keyboard-wedge scanner types the serial and the exact match sorts first.
 */
export function UnitsRegistryPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { roles } = useClaimRoles();
  const storeroom = roles.some((r) => STOREROOM_ROLES.includes(r));
  const admin = roles.includes('admin');
  const [open, setOpen] = useState<{ unitId: string; action: UnitAction } | null>(null);
  const [heldOpen, setHeldOpen] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const catalogItemId = params.get('catalogItemId') ?? '';
  const status = params.get('status') ?? '';
  const controlledOnly = params.get('controlled') === '1';
  const [serial, setSerial] = useState(params.get('serial') ?? '');

  // Debounce typing into the URL, so the search is shareable and the back
  // button works, without a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (serial.trim()) next.set('serial', serial.trim());
      else next.delete('serial');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(handle);
  }, [serial, params, setParams]);

  const query = new URLSearchParams();
  if (catalogItemId) query.set('catalogItemId', catalogItemId);
  if (status) query.set('status', status);
  if (params.get('serial')) query.set('serial', params.get('serial') ?? '');

  const catalog = useApi('/catalog', catalogResponseSchema);
  const units = useApi(`/inventory/units?${query.toString()}`, inventoryUnitsResponseSchema);
  const catalogById = new Map(
    catalog.kind === 'ready' ? catalog.data.items.map((i) => [i.id, i]) : [],
  );

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('inventory.registryTitle')}</h2>
        <Link to="/inventory" className="text-sm text-slate-500 underline-offset-4 hover:underline">
          {t('inventory.backToDashboard')}
        </Link>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          className="latin-inline rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          placeholder={t('inventory.searchSerial')}
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          data-testid="serial-search"
          autoFocus
        />
        <select
          value={catalogItemId}
          onChange={(e) => setFilter('catalogItemId', e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          data-testid="registry-item"
        >
          <option value="">{t('inventory.allItems')}</option>
          {catalog.kind === 'ready' &&
            catalog.data.items
              .filter((i) => i.tracking === 'serialized')
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {itemName(i, i18n.language)}
                </option>
              ))}
        </select>
        <select
          value={status}
          onChange={(e) => setFilter('status', e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          data-testid="registry-status"
        >
          <option value="">{t('inventory.allStatuses')}</option>
          {inventoryStatusSchema.options.map((s) => (
            <option key={s} value={s}>
              {t(`inventory.status.${s}`)}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={controlledOnly}
            onChange={(e) => setFilter('controlled', e.target.checked ? '1' : '')}
            data-testid="registry-controlled-only"
          />
          {t('inventory.controlledOnly')}
        </label>
      </div>

      {units.kind === 'error' && (
        <div className="mt-3">
          <ApiError code={units.code} requestId={units.requestId} />
        </div>
      )}

      {units.kind === 'ready' && (
        <>
          {(() => {
            const shown = controlledOnly
              ? units.data.units.filter((u) => catalogById.get(u.catalogItemId)?.controlled)
              : units.data.units;
            return (
              <>
                <p className="mt-3 text-sm text-slate-500" data-testid="registry-count">
                  {t('inventory.unitCount', { count: shown.length })}
                  {units.data.truncated && ` ${t('inventory.truncated')}`}
                </p>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="registry-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs text-slate-500">
                        <th className="py-2 text-start font-medium">
                          {t('inventory.columns.serial')}
                        </th>
                        <th className="py-2 text-start font-medium">
                          {t('inventory.columns.item')}
                        </th>
                        <th className="py-2 text-start font-medium">
                          {t('inventory.columns.status')}
                        </th>
                        <th className="py-2 text-start font-medium">
                          {t('inventory.columns.condition')}
                        </th>
                        <th className="py-2 text-start font-medium">
                          {t('inventory.columns.holder')}
                        </th>
                        {storeroom && (
                          <th className="py-2 ps-6 text-start font-medium">
                            {t('inventory.columns.actions')}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {shown.map((u) => {
                        const actions = availableUnitActions(u, { storeroom, admin });
                        const isOpen = open?.unitId === u.id;
                        const isHeldOpen = heldOpen === u.id;
                        return (
                          <Fragment key={u.id}>
                            <tr data-testid={`unit-row-${u.id}`}>
                              <td className="py-2 font-medium text-slate-900">
                                <span className="latin-inline">{u.serialNumber}</span>
                              </td>
                              <td className="py-2 text-slate-700">
                                {catalogById.get(u.catalogItemId)
                                  ? itemName(catalogById.get(u.catalogItemId)!, i18n.language)
                                  : u.catalogItemId}
                              </td>
                              <td className="py-2 text-slate-700">
                                {t(`inventory.status.${u.status}`)}
                              </td>
                              <td className="py-2 text-slate-700">
                                {t(`inventory.condition.${u.condition}`)}
                              </td>
                              <td className="py-2 text-slate-500">
                                {u.currentHolderUid ? (
                                  <span className="latin-inline">{u.currentHolderUid}</span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              {storeroom && (
                                <td className="py-2 ps-6 whitespace-nowrap">
                                  {actions.map((a) => (
                                    <button
                                      key={a}
                                      type="button"
                                      onClick={() =>
                                        setOpen(
                                          isOpen && open?.action === a
                                            ? null
                                            : { unitId: u.id, action: a },
                                        )
                                      }
                                      className="me-3 text-xs text-slate-500 underline-offset-4 hover:underline"
                                      data-testid={`unit-action-${a}-${u.id}`}
                                    >
                                      {t(`inventory.${a === 'writeOff' ? 'writeOffUnit' : a}`)}
                                    </button>
                                  ))}
                                  {u.currentHolderUid && (
                                    <button
                                      type="button"
                                      onClick={() => setHeldOpen(isHeldOpen ? null : u.id)}
                                      className="me-3 text-xs text-slate-500 underline-offset-4 hover:underline"
                                      data-testid={`unit-manage-${u.id}`}
                                    >
                                      {t('inventory.manage')}
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                            {isOpen && open && (
                              <tr>
                                <td colSpan={storeroom ? 6 : 5} className="pb-3">
                                  <UnitActionPanel
                                    unit={u}
                                    action={open.action}
                                    onClose={() => setOpen(null)}
                                    onDone={() => {
                                      setOpen(null);
                                      units.reload();
                                    }}
                                  />
                                </td>
                              </tr>
                            )}
                            {isHeldOpen && (
                              <tr>
                                <td colSpan={storeroom ? 6 : 5} className="pb-3">
                                  <HeldUnitActionsArea
                                    unit={u}
                                    viewerUid={user?.uid ?? null}
                                    storeroom={storeroom}
                                    onClose={() => setHeldOpen(null)}
                                    onChanged={() => {
                                      setHeldOpen(null);
                                      units.reload();
                                    }}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </>
      )}
    </section>
  );
}
